import type { TFeature } from '../../features/interfaces';
import { SANDBOX_CLUB_ID, SANDBOX_CLUB_NAME } from '../config';
import { SandboxStore } from '../store';
import { advertisementResolvers } from './resolvers/advertisements';
import { applicationResolvers } from './resolvers/applications';
import { bookingResolvers } from './resolvers/bookings';
import { hostResolvers } from './resolvers/hosts';
import { loyaltyResolvers } from './resolvers/loyalty';
import { paymentResolvers, workshift } from './resolvers/payments';
import { reportResolvers } from './resolvers/reports';
import { shopResolvers } from './resolvers/shop';
import { taskResolvers } from './resolvers/tasks';
import { SandboxOperationHandler, SandboxVariables } from './types';

export type { SandboxOperationHandler, SandboxVariables } from './types';

export class UnknownSandboxOperationError extends Error {
  constructor(operationName: string) {
    super(`Sandbox GraphQL operation is not implemented: ${operationName}`);
    this.name = 'UnknownSandboxOperationError';
  }
}

const permissions = [
  'event_browse',
  'comment_read',
  'client_browse',
  'client_manage',
  'game_account_manage',
  'payment_create',
  'loyalty_program_browse',
  'reports_manage',
  'game_account_browse',
  'host_browse',
  'worker_task_complete',
  'client_session_manage',
  'shortcut_manage',
  'worker_task_manage',
  'worker_manage',
  'good_manage_dispose',
  'cash_order_create',
  'host_manage',
  'setting_browse',
  'work_shift_active',
  'payment_browse',
  'setting_manage',
  'shortcut_browse',
  'worker_task_browse',
  'loyalty_program_manage',
  'promo_code_check',
  'worker_task_all_browse',
  'good_manage_add',
  'work_shift_open',
  'comment_create',
  'game_account_password_view',
  'refund_create',
  'role_browse',
  'shop_manage',
  'can_login_into_work_shift',
  'shop_browse',
  'work_shift_browse',
  'work_shift_close',
  'host_command_manage',
  'booking_manage',
  'role_manage',
  'club_comments_browse',
  'client_info',
  'can_use_debt',
  'worker_client_register',
  'deposit_transfer',
  'user_password_reset',
  'news_manage',
  'advertisement_manage',
] as const;

const permissionObjects = permissions.map((alias, index) => ({
  id: index + 1,
  alias,
}));

const gameAccountRuntime = new WeakMap<
  SandboxStore,
  { anchorMs: number; freedIds: Set<number> }
>();

const getGameAccountRuntime = (
  store: SandboxStore
): { anchorMs: number; freedIds: Set<number> } => {
  const current = gameAccountRuntime.get(store);
  if (current?.anchorMs === store.getAnchorMs()) return current;
  const next = { anchorMs: store.getAnchorMs(), freedIds: new Set<number>() };
  gameAccountRuntime.set(store, next);
  return next;
};

const me = (store: SandboxStore): Record<string, unknown> => ({
  ...store.getWorker(),
  roles: [
    {
      id: 1,
      alias: 'active_operator',
      title: 'Владелец',
      permissions: permissionObjects,
    },
  ],
  permissions: permissionObjects,
});

type SandboxFeature = Exclude<TFeature, 'tech_chat'>;

const defineAllFeatureModules = <T extends readonly SandboxFeature[]>(
  modules: T,
  ...missing: Exclude<SandboxFeature, T[number]> extends never ? [] : [never]
): T => {
  void missing;
  return modules;
};

const featureModules = defineAllFeatureModules([
  'user_comments',
  'telegram_notify',
  'work_shift_events',
  'autostart',
  'goods',
  'pc_security',
  'virtual_hosts',
  'active_window',
  'cash_orders',
  'sold_items',
  'pc_status',
  'work_shift_income',
  'work_shift_stats',
  'wol',
  'applications',
  'per_minute_tariffs',
  'user_history',
  'user_discounts',
  'edit_deposit',
  'user_search',
  'game_accounts',
  'seance_end_action',
  'discounts',
  'package_tariffs',
  'auto_per_minute',
  'worker_tasks',
  'tech_support_vip',
  'pc_disk_usage',
  'shell_tariffs',
  'service_mode',
  'activity_rating',
  'game_accounts_widget',
  'abonements',
  'base_reports',
  'stock_refill',
  'services',
  'ai_tariffs',
  'change_host',
  'tech_support',
  'refunds',
  'work_shift_reports',
  'fast_deposit',
  'pc_comments',
  'host_map',
  'rmb_sell',
  'pc_disk_hide',
  'remote_exec',
  'cash_box',
  'bonus_cash',
  'booking',
  'restrict_chrome_download',
  'removable_storage',
  'club_rules',
  'host_penalty',
  'host_notification',
  'shell_skin',
  'deposit_cashback',
  'self_host_change',
  'shell_skin_screensaver',
  'export_users',
  'call_admin',
  'club_review',
  'remote_control',
  'achievements',
  'combos',
  'client_transaction',
  'free_run',
  'holiday_tariffs',
  'customizable_permissions',
  'post_payments',
  'sbp_tinkoff',
  'sales_report',
  'visits_report',
  'usage_report',
  'smart_bonus',
  'user_report',
  'applications_report',
  'export_goods',
  'shell_goods_display',
  'register_without_verification',
  'kaspi_online',
  'deposit_transfer',
  'stripe',
  'tips',
  'fast_login',
  'club_news',
  'bonus_report',
  'host_hardware',
  'lite_shell',
  'copy_settings',
  'bepaid',
  'advertisement',
] as const);

const encodeBase64 = (value: string): string =>
  window.btoa(unescape(encodeURIComponent(value))).replace(/=+$/g, '');

export const createSandboxToken = (): string => {
  const header = encodeBase64(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = encodeBase64(
    JSON.stringify({ company_id: SANDBOX_CLUB_ID, sandbox: true })
  );
  return `${header}.${payload}.sandbox`;
};

const tokens = (): {
  access_token: string;
  refresh_token: string;
  expires_in: number;
} => ({
  access_token: createSandboxToken(),
  refresh_token: 'sandbox-refresh-token',
  expires_in: 86400,
});

const club = {
  id: SANDBOX_CLUB_ID,
  name: SANDBOX_CLUB_NAME,
  address: 'Москва, м. Бауманская',
  workShiftStatus: 'MY',
  permitted: true,
  tariffName: 'Business',
  operatorFirstName: 'Аврора',
  operatorLastName: 'Администратор',
};

const page = <T>(
  items: T[],
  variables: SandboxVariables
): {
  data: T[];
  paginatorInfo: {
    count: number;
    total: number;
    currentPage: number;
    lastPage: number;
  };
} => {
  const first = Number(variables.first ?? 25);
  const currentPage = Number(variables.page ?? 1);
  const start = Math.max(0, (currentPage - 1) * first);
  return {
    data: items.slice(start, start + first),
    paginatorInfo: {
      count: items.length,
      total: items.length,
      currentPage,
      lastPage: Math.max(1, Math.ceil(items.length / first)),
    },
  };
};

const inputObject = (variables: SandboxVariables): Record<string, unknown> =>
  (variables.input as Record<string, unknown> | undefined) ?? {};

const clientList: SandboxOperationHandler = (variables, store) => {
  const input = inputObject(variables);
  const query = String(input.q ?? '').toLowerCase();
  const filter = input.filter as { groupUUID?: string } | undefined;
  const clients = store.getState().clients.filter((client) => {
    const matchesQuery =
      !query ||
      [client.nickname, client.phone, client.first_name, client.last_name]
        .join(' ')
        .toLowerCase()
        .includes(query);
    const matchesGroup =
      !filter?.groupUUID || client.group?.uuid === filter.groupUUID;
    return matchesQuery && matchesGroup;
  });
  const result = page(clients, variables);
  return {
    clients: {
      ...result,
      total_deposits: clients.reduce((sum, client) => sum + client.deposit, 0),
    },
  };
};

const userGroups = (
  store: SandboxStore
): Array<{
  uuid: string;
  title: string;
  discount: number;
  user_count: number;
  created_at: string;
}> => {
  const groups = new Map<
    string,
    { uuid: string; title: string; discount: number }
  >();
  store.getState().clients.forEach((client) => {
    if (client.group) groups.set(client.group.uuid, client.group);
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    user_count: store
      .getState()
      .clients.filter((client) => client.group?.uuid === group.uuid).length,
    created_at: '2026-06-01T00:00:00.000Z',
  }));
};

const roleList = [
  { id: 1, alias: 'owner' },
  { id: 2, alias: 'manager' },
  { id: 3, alias: 'operator' },
  { id: 4, alias: 'client' },
].map((role) => ({
  ...role,
  permissions: role.alias === 'client' ? [] : permissionObjects,
}));

const clubComments = (
  variables: SandboxVariables,
  store: SandboxStore
): Record<string, unknown> => {
  const input = inputObject(variables);
  const userUuid = input.user_uuid ? String(input.user_uuid) : '';
  const sort = String(input.sort ?? 'NEW_FIRST');
  const reviews = store
    .getState()
    .clubReviews.filter(
      (review) => !userUuid || review.author.uuid === userUuid
    )
    .slice()
    .sort((left, right) => {
      if (sort === 'OLD_FIRST')
        return Date.parse(left.created_at) - Date.parse(right.created_at);
      if (sort === 'HIGH_GRADE_FIRST') return right.grade - left.grade;
      if (sort === 'LOW_GRADE_FIRST') return left.grade - right.grade;
      return Date.parse(right.created_at) - Date.parse(left.created_at);
    });
  const gradeCount = (grade: number): number =>
    reviews.filter((review) => review.grade === grade).length;
  return {
    ...page(reviews, variables),
    summary: {
      average_grade: reviews.length
        ? reviews.reduce((sum, review) => sum + review.grade, 0) /
          reviews.length
        : 0,
      unread_total: reviews.filter((review) => !review.is_read).length,
      five: gradeCount(5),
      four: gradeCount(4),
      three: gradeCount(3),
      two: gradeCount(2),
      one: gradeCount(1),
    },
  };
};

const operationHandlers: Record<string, SandboxOperationHandler> = {
  userClubs: (variables) => {
    const input = inputObject(variables);
    if (
      Object.keys(input).length > 0 &&
      (!String(input.login ?? '').trim() ||
        !String(input.password ?? '').trim())
    ) {
      throw new Error('Введите логин и пароль');
    }
    return { userClubs: [club] };
  },
  login: (variables) => {
    const input = inputObject(variables);
    if (
      !String(input.login ?? '').trim() ||
      !String(input.password ?? '').trim()
    ) {
      throw new Error('Введите логин и пароль');
    }
    return { login: tokens() };
  },
  relogin: () => ({ relogin: tokens() }),
  RefreshToken: () => ({ refreshToken: tokens() }),
  LogoutAllV2: () => ({ logoutAll: true }),
  me: (_, store) => ({ me: me(store) }),
  featureFlags: (_, store) => ({
    featureFlags: {
      subscription: 'Business',
      host_count: 36,
      expires_at: new Date(
        store.getAnchorMs() + 10 * 365 * 86400000
      ).toISOString(),
      modules: featureModules,
      it_adviser_support: false,
      shortcuts_sync_enabled: false,
      company_owner: { inn: 0 },
      verification: { status: 'VERIFIED' },
    },
  }),
  settingValues: (_, store) => ({
    settingValues: store.getState().settings,
    currencies: [
      { id: 1, title: 'Российский рубль', letter: '₽', alias: 'RUB' },
    ],
  }),
  setSetting: (variables, store) => {
    const input = inputObject(variables);
    const alias = String(input.alias ?? '');
    if (!alias) throw new Error('Не задан alias настройки');
    return {
      setSetting: store.setSetting(
        alias,
        input.value === null ? null : String(input.value ?? '')
      ),
    };
  },
  SetSettings: (variables, store) => {
    const list =
      (inputObject(variables).list as Array<Record<string, unknown>>) ?? [];
    const settings = list.map((input) => {
      const alias = String(input.alias ?? '');
      if (!alias) throw new Error('Не задан alias настройки');
      return {
        alias,
        value: input.value === null ? null : String(input.value ?? ''),
      };
    });
    return { setSettings: store.setSettings(settings) };
  },
  Now: (_, store) => ({ now: store.getCurrentTimestamp() }),
  versions: () => ({
    versions: { easter_egg: false, backend: 'sandbox-39.0.1' },
  }),
  canUseDebt: () => ({ canUseDebt: false }),
  news: () => ({ news: { data: [] } }),
  hostsStatuses: () => ({
    hostsOverview: Array.from({ length: 12 }, (_, index) => ({
      id: index + 1,
      alias: `PC-${String(index + 1).padStart(2, '0')}`,
      group_id: index < 8 ? 1 : 2,
      online: index !== 10,
      in_service: index === 11,
      client_sessions: index < 4 ? [{ id: 400 + index }] : [],
      shell_mode: index === 10 ? 'OFF' : 'NORMAL',
      user: null,
    })),
  }),
  adminCalls: () => ({
    hostGroupsOverview: [
      { id: 1, title: 'Main Zone' },
      { id: 2, title: 'VIP Lounge' },
    ],
    hostsOverview: [],
  }),
  clubCommentsCount: (variables, store) => ({
    clubComments: clubComments(variables, store),
  }),
  clubReviews: (variables, store) => ({
    clubComments: clubComments(variables, store),
  }),
  readClubComment: (variables, store) => {
    const review = store
      .getState()
      .clubReviews.find((item) => item.id === Number(variables.id));
    if (!review) throw new Error('Отзыв не найден');
    review.is_read = true;
    return { readClubComment: review };
  },
  readAllClubComments: (_, store) => {
    store.getState().clubReviews.forEach((review) => (review.is_read = true));
    return { readAllClubComments: true };
  },
  CompanyNewsList: (variables, store) => {
    const input = inputObject(variables);
    const hasActive = typeof input.active === 'boolean';
    const items = store
      .getState()
      .companyNews.filter((item) => !hasActive || item.active === input.active);
    return { companyNewsList: page(items, variables) };
  },
  CompanyNewsArticle: (variables, store) => {
    const article = store
      .getState()
      .companyNews.find(
        (item) => item.id === String(variables.companyNewsArticleId)
      );
    if (!article) throw new Error('Новость не найдена');
    return { companyNewsArticle: article };
  },
  UpdateCompanyNews: (variables, store) => {
    const article = store
      .getState()
      .companyNews.find(
        (item) => item.id === String(variables.updateCompanyNewsId)
      );
    if (!article) throw new Error('Новость не найдена');

    const input = inputObject(variables);
    const link = input.link as Record<string, unknown> | null | undefined;
    const publicationPeriod = input.publication_period as
      | Record<string, unknown>
      | null
      | undefined;

    Object.assign(article, {
      active: Boolean(input.active),
      content: String(input.content ?? ''),
      image: String(input.image ?? ''),
      link: link
        ? { label: String(link.label ?? ''), url: String(link.url ?? '') }
        : null,
      publication_period: publicationPeriod
        ? {
            from: publicationPeriod.from
              ? String(publicationPeriod.from)
              : null,
            until: publicationPeriod.until
              ? String(publicationPeriod.until)
              : null,
          }
        : null,
      title: String(input.title ?? ''),
      updated_at: store.getCurrentTimestamp(),
    });

    return { updateCompanyNews: article };
  },
  getPostPaymentCount: (_, store) => ({ activeWorkShift: workshift(store) }),
  getWorkShiftPaymentOverviewData: (_, store) => ({
    getWorkShiftPaymentOverviewData: workshift(store),
  }),
  getWorkshiftId: (_, store) => ({
    getWorkShiftPaymentOverviewData: workshift(store),
  }),
  getMoneyByType: (_, store) => ({
    getWorkShiftPaymentOverviewData: workshift(store),
  }),
  getMovementMoney: (_, store) => ({
    getWorkShiftPaymentOverviewData: workshift(store),
  }),
  getDeposit: (_, store) => ({
    getWorkShiftPaymentOverviewData: workshift(store),
  }),
  workshiftWorkerData: (_, store) => ({
    getWorkShiftPaymentOverviewData: workshift(store),
  }),
  activeWorkShift: (_, store) => ({ activeWorkShift: workshift(store) }),
  workshiftPayments: (_, store) => ({ activeWorkShift: workshift(store) }),
  getActiveClientList: (_, store) => ({
    getActiveClientList: store
      .getState()
      .clients.slice(0, 4)
      .map((client) => ({
        nickname: client.nickname,
        payments: 0,
        total_seconds: client.total_hours * 3600,
        last_client_activity: client.last_client_activity,
        avatar_url: client.avatar_url,
        roles: client.roles,
      })),
  }),
  gameAccountGroups: (_, store) => {
    const hosts = store.getState().hosts;
    const { freedIds } = getGameAccountRuntime(store);
    const steamGroup = { id: 1, title: 'Steam' };
    const epicGroup = { id: 2, title: 'Epic Games' };
    const account = (
      id: number,
      login: string,
      group: typeof steamGroup,
      hostId?: number
    ): Record<string, unknown> => {
      const host = hosts.find((item) => item.id === hostId);
      const busy = Boolean(host) && !freedIds.has(id);
      const assignedHost = host
        ? { id: host.id, alias: host.alias, group_id: host.group_id }
        : undefined;

      return {
        __typename: 'GameAccount',
        id,
        login,
        password: 'demo-password',
        active: true,
        status: busy ? 'IN_GAME' : 'ONLINE',
        busy,
        group,
        host: busy ? assignedHost : undefined,
        assignment: busy && assignedHost ? { host: assignedHost } : undefined,
      };
    };

    return {
      gameAccountGroups: [
        {
          __typename: 'GameAccountGroup',
          ...steamGroup,
          launcher: 'STEAM',
          accounts: [
            account(1, 'smart-club-01', steamGroup, 1),
            account(2, 'smart-club-02', steamGroup),
            account(3, 'smart-club-03', steamGroup, 14),
          ],
          shortcuts: [{ id: 1, title: 'Counter-Strike 2' }],
        },
        {
          __typename: 'GameAccountGroup',
          ...epicGroup,
          launcher: 'EGS',
          accounts: [
            account(4, 'smart-epic-01', epicGroup),
            account(5, 'smart-epic-02', epicGroup, 24),
          ],
          shortcuts: [{ id: 2, title: 'Fortnite' }],
        },
      ],
    };
  },
  freeGameAccount: (variables, store) => {
    const id = Number(variables.id);
    getGameAccountRuntime(store).freedIds.add(id);
    return {
      freeGameAccount: {
        __typename: 'GameAccount',
        id,
        busy: false,
      },
    };
  },
  clients: clientList,
  workers: (variables, store) => {
    const input = inputObject(variables);
    const query = String(input.q ?? input.search ?? '').toLowerCase();
    const workers = store
      .getState()
      .workers.filter(
        (worker) =>
          !query ||
          [worker.nickname, worker.phone, worker.first_name, worker.last_name]
            .join(' ')
            .toLowerCase()
            .includes(query)
      );
    return { workers: page(workers, variables) };
  },
  roles: () => ({ roles: roleList }),
  totalClients: (variables, store) => {
    const result = clientList(variables, store).clients as {
      total_deposits: number;
      paginatorInfo: { total: number };
    };
    return {
      clients: {
        total_deposits: result.total_deposits,
        paginatorInfo: { total: result.paginatorInfo.total },
      },
    };
  },
  searchClients: clientList,
  user: (variables, store) => {
    const uuid = String(variables.uuid);
    const client = store.getState().clients.find((item) => item.uuid === uuid);
    const worker = store.getState().workers.find((item) => item.uuid === uuid);
    if (!client && !worker) throw new Error('Пользователь не найден');
    return {
      user: client ?? {
        ...worker,
        deposit: 0,
        last_comment: null,
        pausable_info: null,
      },
    };
  },
  userGroups: (_, store) => ({ userGroups: userGroups(store) }),
  getGroupTitles: (_, store) => ({ userGroups: userGroups(store) }),
  pausableClientSessions: () => ({ pausableClientSessions: [] }),
  setDeposit: (variables, store) => {
    const input = inputObject(variables);
    const client = store.setDeposit(
      String(input.client_uuid),
      Number(input.value)
    );
    return {
      setDeposit: {
        __typename: 'User',
        uuid: client.uuid,
        deposit: client.deposit,
      },
    };
  },
  SetBonus: (variables, store) => {
    const input = inputObject(variables);
    const client = store.setBonus(
      String(input.client_uuid),
      Number(input.value)
    );
    return {
      setBonus: {
        __typename: 'User',
        uuid: client.uuid,
        bonus: client.bonus,
      },
    };
  },
  setUserDiscount: (variables, store) => {
    const input = inputObject(variables);
    const client = store.setUserDiscount(
      String(input.client_uuid),
      Number(input.value)
    );
    return {
      setUserDiscount: {
        __typename: 'User',
        uuid: client.uuid,
        user_discount: client.user_discount,
        discounts: client.discounts,
      },
    };
  },
  GetBonusHistory: (variables, store) => ({
    getBonusHistory: page(
      store.getBonusHistory(String(variables.uuid)),
      variables
    ),
  }),
  banClient: (variables, store) => {
    const input = inputObject(variables);
    const client = store.setClientBanned(
      String(variables.uuid),
      Boolean(input.value)
    );
    return { banClient: client };
  },
  validatePayment: () => ({ validatePayment: true }),
  createPayment: (variables, store) => {
    const payment = store.createPayment(
      inputObject(variables) as unknown as Parameters<
        SandboxStore['createPayment']
      >[0]
    );
    return { createPayment: { id: payment.id } };
  },
  getPaymentsByClientId: (variables, store) => {
    const payments = store
      .getState()
      .payments.filter(
        (payment) => payment.client?.uuid === String(variables.uuid)
      )
      .map((payment) => ({
        ...payment,
        title: payment.items.some((item) => item.type === 'DEPOSIT')
          ? 'Пополнение депозита'
          : 'Платеж',
        amount: 1,
        paymentMethod: payment.method,
        worker: me(store),
      }));
    return { getPaymentsByClientId: page(payments, variables) };
  },
  GetClientDepositTransfers: () => ({
    getClientDepositTransfers: {
      data: [],
      paginatorInfo: { currentPage: 1, lastPage: 1 },
    },
  }),
  cashBoxLicenseToken: () => ({ cashBoxLicenseToken: '' }),
  ...hostResolvers,
  ...bookingResolvers,
  ...shopResolvers,
  ...taskResolvers,
  ...paymentResolvers,
  ...advertisementResolvers,
  ...applicationResolvers,
  ...loyaltyResolvers,
  ...reportResolvers,
};

export const resolveSandboxOperation = (
  operationName: string,
  variables: SandboxVariables,
  store: SandboxStore
): Record<string, unknown> => {
  const handler = operationHandlers[operationName];
  if (!handler)
    throw new UnknownSandboxOperationError(operationName || '<anonymous>');
  return handler(variables, store);
};

export const sandboxOperationNames = Object.freeze(
  Object.keys(operationHandlers).sort()
);
